import React from 'react';
import { Mail, Globe, ExternalLink } from 'lucide-react';
import { sharedStyles } from './shared/sharedStyles.js';

/**
 * ShowMatchExpandedDetails - Expanded details overlay for a show recommendation
 *
 * GUEST-SIDE mirror of GuestExpandedDetails.jsx. Shows full information
 * about a recommended show when the row is expanded.
 */
const ShowMatchExpandedDetails = ({ show, hasPremiumAccess }) => {
  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        marginTop: '-1px',
        padding: '1.5rem',
        background: 'rgba(255, 255, 255, 0.98)',
        backdropFilter: 'blur(10px)',
        border: '2px solid rgba(234, 88, 12, 0.2)',
        borderRadius: '0 0 12px 12px',
        boxShadow: '0 10px 40px rgba(234, 88, 12, 0.15)',
        zIndex: 200
      }}
    >
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '1.5rem'
      }}>
        {/* Show Information */}
        <div>
          <h4 style={{
            fontSize: '0.75rem',
            fontWeight: '600',
            color: sharedStyles.colors.textSecondary,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '0.75rem'
          }}>
            Show Information
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div>
              <div style={{ fontSize: '0.7rem', color: sharedStyles.colors.textTertiary, marginBottom: '0.125rem' }}>Show Name</div>
              <div style={{ fontSize: '0.875rem', fontWeight: '600', color: sharedStyles.colors.textPrimary }}>{show.showName}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.7rem', color: sharedStyles.colors.textTertiary, marginBottom: '0.125rem' }}>Host</div>
              <div style={{ fontSize: '0.875rem', color: sharedStyles.colors.textSecondary }}>{show.hostName}</div>
            </div>
            {show.network && (
              <div>
                <div style={{ fontSize: '0.7rem', color: sharedStyles.colors.textTertiary, marginBottom: '0.125rem' }}>Network</div>
                <div style={{ fontSize: '0.875rem', color: sharedStyles.colors.textSecondary }}>{show.network}</div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '1.5rem' }}>
              <div>
                <div style={{ fontSize: '0.7rem', color: sharedStyles.colors.textTertiary, marginBottom: '0.125rem' }}>Match Score</div>
                <div style={{ fontSize: '1.125rem', fontWeight: '700', color: sharedStyles.colors.primary }}>
                  {(show.relevanceScore * 10).toFixed(1)} / 10
                </div>
              </div>
              {show.accessibilityScore && (
                <div>
                  <div style={{ fontSize: '0.7rem', color: sharedStyles.colors.textTertiary, marginBottom: '0.125rem' }}>Accessibility</div>
                  <div style={{ fontSize: '1.125rem', fontWeight: '700', color: sharedStyles.colors.success }}>
                    {(show.accessibilityScore * 10).toFixed(1)} / 10
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Why Good Fit */}
        <div>
          <h4 style={{
            fontSize: '0.75rem',
            fontWeight: '600',
            color: sharedStyles.colors.textSecondary,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '0.75rem'
          }}>
            Why This Show Is A Good Fit
          </h4>
          <p style={{
            fontSize: '0.875rem',
            color: sharedStyles.colors.textSecondary,
            lineHeight: '1.6',
            textAlign: 'left',
            margin: 0,
            paddingLeft: '0.5rem'
          }}>
            {show.whyGoodFit}
          </p>
        </div>

        {/* Topics Covered */}
        {show.topicsCovered && show.topicsCovered.length > 0 && (
          <div>
            <h4 style={{
              fontSize: '0.75rem',
              fontWeight: '600',
              color: sharedStyles.colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '0.75rem'
            }}>
              Topics Covered
            </h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {show.topicsCovered.map((topic, idx) => (
                <span key={idx} style={{
                  background: 'rgba(234, 88, 12, 0.1)',
                  color: sharedStyles.colors.accentBrown,
                  padding: '0.25rem 0.75rem',
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  fontWeight: '500'
                }}>
                  {topic}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Show Details */}
        <div>
          <h4 style={{
            fontSize: '0.75rem',
            fontWeight: '600',
            color: sharedStyles.colors.textSecondary,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '0.75rem'
          }}>
            Show Details
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {show.audienceSize && (
              <div>
                <div style={{ fontSize: '0.7rem', color: sharedStyles.colors.textTertiary, marginBottom: '0.125rem' }}>Audience Size</div>
                <div style={{ fontSize: '0.875rem', color: sharedStyles.colors.textSecondary, textTransform: 'capitalize' }}>{show.audienceSize}</div>
              </div>
            )}
            {show.showTier && (
              <div>
                <div style={{ fontSize: '0.7rem', color: sharedStyles.colors.textTertiary, marginBottom: '0.125rem' }}>Show Tier</div>
                <div style={{ fontSize: '0.875rem', color: sharedStyles.colors.textSecondary, textTransform: 'capitalize' }}>{show.showTier}</div>
              </div>
            )}
            {show.description && (
              <div>
                <div style={{ fontSize: '0.7rem', color: sharedStyles.colors.textTertiary, marginBottom: '0.125rem' }}>Description</div>
                <div style={{ fontSize: '0.875rem', color: sharedStyles.colors.textSecondary, lineHeight: '1.5' }}>{show.description}</div>
              </div>
            )}
          </div>
        </div>

        {/* Contact Information (Premium) */}
        {hasPremiumAccess && (
          <div>
            <h4 style={{
              fontSize: '0.75rem',
              fontWeight: '600',
              color: sharedStyles.colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '0.75rem'
            }}>
              Contact Information
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {show.contactEmail && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Mail size={14} style={{ color: sharedStyles.colors.primary }} />
                  <a
                    href={`mailto:${show.contactEmail}`}
                    style={{ fontSize: '0.875rem', color: sharedStyles.colors.primary, textDecoration: 'none' }}
                  >
                    {show.contactEmail}
                  </a>
                </div>
              )}
              {show.website && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Globe size={14} style={{ color: sharedStyles.colors.primary }} />
                  <a
                    href={show.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: '0.875rem', color: sharedStyles.colors.primary, textDecoration: 'none' }}
                  >
                    {show.website.replace('https://', '').replace('http://', '')}
                  </a>
                </div>
              )}
              {show.pitchFormUrl && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <ExternalLink size={14} style={{ color: sharedStyles.colors.primary }} />
                  <a
                    href={show.pitchFormUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: '0.875rem', color: sharedStyles.colors.primary, textDecoration: 'none' }}
                  >
                    Pitch Submission Form
                  </a>
                </div>
              )}
              {!show.contactEmail && !show.website && !show.pitchFormUrl && (
                <div style={{ fontSize: '0.875rem', color: sharedStyles.colors.textTertiary }}>
                  No contact information available
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShowMatchExpandedDetails;
